import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Eye, Phone, MapPin, Calendar, User, Image, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminIdentityVerifications = () => {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingVerifications();
  }, []);

  const fetchPendingVerifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/admin/pending-verifications`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setVerifications(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch verifications:', error);
      let errorMessage = "Failed to load pending verifications";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationDetails = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/admin/verification-details/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSelectedUser(response.data.user);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Failed to fetch verification details:', error);
      let errorMessage = "Failed to load verification details";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleApprove = async (userId) => {
    if (!window.confirm('Are you sure you want to approve this user? They will gain full platform access.')) {
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/verify-user/${userId}`,
        {
          action: 'approve',
          notes: adminNotes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: "Success",
        description: response.data.message,
      });

      setShowDetailsModal(false);
      setAdminNotes('');
      fetchPendingVerifications();
    } catch (error) {
      console.error('Failed to approve verification:', error);
      // Extract error message properly
      let errorMessage = "Failed to approve verification";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle if detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => typeof err === 'string' ? err : err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (userId) => {
    if (!rejectionReason.trim()) {
      toast({
        title: "Rejection Reason Required",
        description: "Please provide a reason for rejection",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/verify-user/${userId}`,
        {
          action: 'reject',
          reason: rejectionReason,
          notes: adminNotes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: "Success",
        description: response.data.message,
      });

      setShowDetailsModal(false);
      setShowRejectModal(false);
      setRejectionReason('');
      setAdminNotes('');
      fetchPendingVerifications();
    } catch (error) {
      console.error('Failed to reject verification:', error);
      // Extract error message properly
      let errorMessage = "Failed to reject verification";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle if detail is an array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => typeof err === 'string' ? err : err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Identity Verifications</h1>
        <p className="text-gray-600 mt-2">Review and approve user identity documents</p>
      </div>

      {/* Stats Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Verifications</p>
                <p className="text-3xl font-bold text-yellow-600">{verifications.length}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Verifications List */}
      {verifications.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">All Caught Up!</h3>
            <p className="text-gray-600">No pending identity verifications at this time.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {verifications.map((user) => (
                <div
                  key={user.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <User className="w-5 h-5 text-gray-400" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          {user.firstName} {user.lastName}
                        </h3>
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                          Pending
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <span>{user.phone || 'No phone'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{user.address?.city || 'No city'}, {user.address?.province || 'No province'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>Submitted: {formatDate(user.submittedAt)}</span>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={() => fetchVerificationDetails(user.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Review
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-gray-900">Verification Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="absolute top-6 right-6 text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Profile Info Comparison Banner */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-amber-800 mb-3 flex items-center gap-2">
                  ⚠️ Match Profile Information with ID Document
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Name */}
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <p className="text-xs text-amber-600 font-medium uppercase">Full Name</p>
                    <p className="text-lg font-bold text-gray-900">
                      {selectedUser.firstName} {selectedUser.lastName}
                    </p>
                  </div>
                  
                  {/* Date of Birth */}
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <p className="text-xs text-amber-600 font-medium uppercase">Date of Birth</p>
                    <p className="text-lg font-bold text-gray-900">
                      {selectedUser.dob || 'Not provided'}
                    </p>
                    {selectedUser.age && (
                      <p className="text-xs text-gray-500">Age: {selectedUser.age} years</p>
                    )}
                  </div>
                  
                  {/* Province */}
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <p className="text-xs text-amber-600 font-medium uppercase">Province/State</p>
                    <p className="text-lg font-bold text-gray-900">
                      {selectedUser.address?.province || selectedUser.province || 'Not provided'}
                    </p>
                  </div>
                  
                  {/* Country */}
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <p className="text-xs text-amber-600 font-medium uppercase">Country</p>
                    <p className="text-lg font-bold text-gray-900">
                      {selectedUser.address?.country || selectedUser.country || 'Not provided'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <label className="text-gray-600">Email</label>
                    <p className="text-gray-900 font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="text-gray-600">Gender</label>
                    <p className="text-gray-900 font-medium capitalize">{selectedUser.gender || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-gray-600">Phone Number</label>
                    <p className="text-gray-900 font-medium">{selectedUser.phone || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Full Address if available */}
              {selectedUser.address && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Full Address</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900">
                      {selectedUser.address.street && <>{selectedUser.address.street}<br /></>}
                      {selectedUser.address.city}, {selectedUser.address.province}<br />
                      {selectedUser.address.postalCode && <>{selectedUser.address.postalCode}<br /></>}
                      {selectedUser.address.country}
                    </p>
                  </div>
                </div>
              )}

              {/* Profile Picture */}
              {selectedUser.documents?.profilePicture && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Picture</h3>
                  <div className="flex justify-center">
                    <a 
                      href={selectedUser.documents.profilePicture.startsWith('http') ? selectedUser.documents.profilePicture : `${API_URL}${selectedUser.documents.profilePicture}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block cursor-pointer hover:opacity-80 transition-opacity"
                    >
                      <img
                        src={selectedUser.documents.profilePicture.startsWith('http') ? selectedUser.documents.profilePicture : `${API_URL}${selectedUser.documents.profilePicture}`}
                        alt="Profile"
                        className="w-48 h-48 object-cover rounded-full border-4 border-gray-300"
                        onError={(e) => {
                          console.error('Failed to load Profile Picture:', selectedUser.documents.profilePicture);
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="gray">Image Not Found</text></svg>';
                        }}
                      />
                      <p className="text-xs text-blue-600 mt-2 text-center hover:underline">Click to view full size</p>
                    </a>
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    🔒 This will be the user's locked profile picture
                  </p>
                </div>
              )}

              {/* ID Documents */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Identity Documents</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* ID Front */}
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-2 block">ID Front</label>
                    {selectedUser.documents?.idFront ? (
                      <a 
                        href={selectedUser.documents.idFront.startsWith('http') ? selectedUser.documents.idFront : `${API_URL}${selectedUser.documents.idFront}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block cursor-pointer hover:opacity-80 transition-opacity"
                      >
                        <img
                          src={selectedUser.documents.idFront.startsWith('http') ? selectedUser.documents.idFront : `${API_URL}${selectedUser.documents.idFront}`}
                          alt="ID Front"
                          className="w-full h-48 object-cover rounded-lg border border-gray-300"
                          onError={(e) => {
                            console.error('Failed to load ID Front image:', selectedUser.documents.idFront);
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="gray">Image Not Found</text></svg>';
                          }}
                        />
                        <p className="text-xs text-blue-600 mt-1 hover:underline">Click to view full size</p>
                      </a>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Image className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* ID Back */}
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-2 block">ID Back</label>
                    {selectedUser.documents?.idBack ? (
                      <a 
                        href={selectedUser.documents.idBack.startsWith('http') ? selectedUser.documents.idBack : `${API_URL}${selectedUser.documents.idBack}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block cursor-pointer hover:opacity-80 transition-opacity"
                      >
                        <img
                          src={selectedUser.documents.idBack.startsWith('http') ? selectedUser.documents.idBack : `${API_URL}${selectedUser.documents.idBack}`}
                          alt="ID Back"
                          className="w-full h-48 object-cover rounded-lg border border-gray-300"
                          onError={(e) => {
                            console.error('Failed to load ID Back image:', selectedUser.documents.idBack);
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="gray">Image Not Found</text></svg>';
                          }}
                        />
                        <p className="text-xs text-blue-600 mt-1 hover:underline">Click to view full size</p>
                      </a>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Image className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Selfie with ID */}
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-2 block">Selfie with ID</label>
                    {selectedUser.documents?.selfie ? (
                      <a 
                        href={selectedUser.documents.selfie.startsWith('http') ? selectedUser.documents.selfie : `${API_URL}${selectedUser.documents.selfie}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block cursor-pointer hover:opacity-80 transition-opacity"
                      >
                        <img
                          src={selectedUser.documents.selfie.startsWith('http') ? selectedUser.documents.selfie : `${API_URL}${selectedUser.documents.selfie}`}
                          alt="Selfie"
                          className="w-full h-48 object-cover rounded-lg border border-gray-300"
                          onError={(e) => {
                            console.error('Failed to load Selfie image:', selectedUser.documents.selfie);
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="gray">Image Not Found</text></svg>';
                          }}
                        />
                        <p className="text-xs text-blue-600 mt-1 hover:underline">Click to view full size</p>
                      </a>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Image className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="text-sm font-medium text-gray-600 mb-2 block">Admin Notes (Optional)</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  rows="3"
                  placeholder="Add any notes about this verification..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t border-gray-200">
                <Button
                  onClick={() => handleApprove(selectedUser.id)}
                  disabled={processing}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {processing ? 'Processing...' : 'Approve'}
                </Button>
                <Button
                  onClick={() => setShowRejectModal(true)}
                  disabled={processing}
                  className="flex-1 bg-mint-500 hover:bg-mint-600 text-white"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => setShowDetailsModal(false)}
                  disabled={processing}
                  className="px-8 bg-gray-300 hover:bg-gray-400 text-gray-800"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Reject Verification</h3>
            <p className="text-gray-600 mb-4">Please provide a reason for rejection. The user will receive this in an email.</p>
            
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent mb-4"
              rows="4"
              placeholder="e.g., ID photo is unclear, information does not match, etc."
              required
            />

            <div className="flex gap-4">
              <Button
                onClick={() => handleReject(selectedUser.id)}
                disabled={processing || !rejectionReason.trim()}
                className="flex-1 bg-mint-500 hover:bg-mint-600 text-white"
              >
                {processing ? 'Processing...' : 'Confirm Rejection'}
              </Button>
              <Button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                disabled={processing}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminIdentityVerifications;
